import { useMemo, useState } from 'react';
import { Lock, Play, Rocket, Sparkles, Check, Infinity as InfinityIcon, Crown } from 'lucide-react';
import { LEVELS, BIOMES, SHIPS } from '@/lib/peaceDefenderLevels';
import StarBadge from './StarBadge';

/**
 * PeaceDefenderLevelSelect — 6 biomes × 5 levels, plus a Hangar strip
 * for picking / unlocking ships.
 *
 * props:
 *   pd          — loungeMeta.pd (levelStars, highestUnlocked, hangar, modesUnlocked)
 *   fp          — current focus points
 *   onPick      — (levelId) => void
 *   onEndless   — () => void
 *   onMaster    — () => void
 *   onUnlockShip— (ship) => void
 *   onEquipShip — (shipId) => void
 */
export default function PeaceDefenderLevelSelect({
  pd, fp = 0, onPick, onEndless, onMaster, onUnlockShip, onEquipShip,
}) {
  const grouped = useMemo(() => BIOMES.map(b => ({
    ...b,
    levels: LEVELS.filter(l => l.biome === b.id),
  })), []);

  // Gated progression — clear a level to unlock the next.
  const highestUnlocked = pd?.highestUnlocked ?? 1;
  const [tab, setTab] = useState('levels'); // levels | hangar

  return (
    <div className="space-y-6" data-testid="pd-level-select">
      {/* Tabs — Levels / Hangar */}
      <div className="flex items-center gap-1 p-1 rounded-full bg-black/40 backdrop-blur-md border border-white/10 w-fit mx-auto">
        <button type="button" onClick={() => setTab('levels')} data-testid="pd-tab-levels"
                className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-smooth ${
                  tab === 'levels' ? 'bg-white/90 text-navy' : 'text-white/60 hover:text-white'
                }`}>
          Levels
        </button>
        <button type="button" onClick={() => setTab('hangar')} data-testid="pd-tab-hangar"
                className={`px-4 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5 transition-smooth ${
                  tab === 'hangar' ? 'bg-white/90 text-navy' : 'text-white/60 hover:text-white'
                }`}>
          <Rocket size={12} strokeWidth={2.2}/> Hangar
        </button>
      </div>

      {tab === 'levels' && (
        <>
          {grouped.map((b) => (
            <div key={b.id} className="rounded-2xl p-4 lg:p-5 border border-white/5"
                 style={{ background: `linear-gradient(135deg, ${b.bg[0]} 0%, ${b.bg[1]} 100%)` }}>
              <div className="flex items-center justify-between mb-3">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.32em] font-semibold" style={{ color: b.accent }}>
                    Biome {b.id}
                  </p>
                  <h4 className="font-heading text-xl text-white mt-0.5">{b.name}</h4>
                  <p className="text-white/55 text-xs mt-0.5">{b.tagline}</p>
                </div>
                <div className="text-right">
                  <p className="text-[9px] uppercase tracking-widest text-white/40">Stars</p>
                  <p className="font-heading text-lg text-white tabular-nums">
                    {b.levels.reduce((sum, l) => sum + (pd?.levelStars?.[l.id] ?? 0), 0)}
                    <span className="text-white/40">/{b.levels.length * 3}</span>
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-5 gap-2">
                {b.levels.map((l) => {
                  const stars = pd?.levelStars?.[l.id] ?? 0;
                  const locked = l.id > highestUnlocked;
                  return (
                    <button
                      key={l.id}
                      type="button"
                      disabled={locked}
                      onClick={() => onPick(l.id)}
                      data-testid={`pd-level-${l.id}`}
                      className={`relative rounded-xl px-2 py-2.5 text-center transition-all border ${
                        locked
                          ? 'bg-white/5 border-white/5 cursor-not-allowed'
                          : stars > 0
                            ? 'bg-white/10 border-white/15 hover:bg-white/15 hover:-translate-y-0.5'
                            : 'bg-white/[0.06] border-white/10 hover:bg-white/10 hover:-translate-y-0.5'
                      }`}
                      style={l.isBoss && !locked ? {
                        boxShadow: `0 0 0 1px ${b.glow}55, inset 0 0 12px ${b.glow}22`,
                      } : undefined}
                    >
                      <p className={`font-heading text-lg tabular-nums leading-none ${locked ? 'text-white/25' : 'text-white'}`}>
                        {l.id}
                      </p>
                      <div className="mt-1 flex items-center justify-center min-h-[14px]">
                        {locked
                          ? <Lock size={11} className="text-white/25" strokeWidth={2}/>
                          : <StarBadge count={stars} size={9} testid={`pd-level-${l.id}-stars`}/>}
                      </div>
                      {l.isBoss && !locked && (
                        <span className="absolute -top-1.5 -right-1.5 text-[8px] font-bold px-1.5 py-0.5 rounded-full"
                              style={{ background: b.glow, color: '#0B0F1E' }}>
                          BOSS
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Mode unlocks — Endless & Master */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <button type="button" onClick={onEndless} data-testid="pd-endless-mode"
              className="rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#0F1A2E] to-[#3a0f7a] border-fuchsia-400/30 hover:border-fuchsia-400/60 hover:-translate-y-0.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-fuchsia-300 flex items-center gap-1.5">
                    <InfinityIcon size={12} strokeWidth={2}/> Endless Peace
                  </p>
                  <p className="font-heading text-lg mt-1 text-white">Survive as long as you can</p>
                  <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
                    No wave limit. Rocks scale. Just you, your ship, and the neon.
                  </p>
                </div>
                <Play size={16} className="text-fuchsia-300 mt-1 shrink-0" strokeWidth={2}/>
              </div>
              {(pd?.endlessBest ?? 0) > 0 && (
                <p className="text-white/60 text-xs mt-2">Best: <span className="text-fuchsia-300 tabular-nums">{pd.endlessBest}</span> kills</p>
              )}
            </button>

            <button type="button" onClick={onMaster} data-testid="pd-master-mode"
              className="rounded-2xl p-4 text-left transition-all border bg-gradient-to-br from-[#04162a] to-[#7d1e5a] border-cyan-300/30 hover:border-cyan-300/60 hover:-translate-y-0.5">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.28em] font-semibold text-cyan-300 flex items-center gap-1.5">
                    <Crown size={12} strokeWidth={2}/> Master Peace
                  </p>
                  <p className="font-heading text-lg mt-1 text-white">The prism challenge</p>
                  <p className="text-white/60 text-xs mt-1.5 leading-relaxed">
                    Every biome bleeding at once. Only one life. Made for masters.
                  </p>
                </div>
                <Sparkles size={16} className="text-cyan-300 mt-1 shrink-0" strokeWidth={2}/>
              </div>
              {(pd?.masterBest ?? 0) > 0 && (
                <p className="text-white/60 text-xs mt-2">Best: <span className="text-cyan-300 tabular-nums">{pd.masterBest}</span> kills</p>
              )}
            </button>
          </div>
        </>
      )}

      {tab === 'hangar' && (
        <div className="rounded-2xl p-4 lg:p-5 border border-white/5"
             style={{ background: 'linear-gradient(135deg, #050914 0%, #131b3a 100%)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-cyan-300 font-semibold">Hangar</p>
              <h4 className="font-heading text-xl text-white mt-0.5">Choose your ship</h4>
              <p className="text-white/55 text-xs mt-0.5">Unlock with focus points + level clears.</p>
            </div>
            <div className="text-right">
              <p className="text-[9px] uppercase tracking-widest text-white/40">Focus Points</p>
              <p className="font-heading text-lg text-gold tabular-nums" data-testid="pd-hangar-fp">{fp}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {SHIPS.map(ship => {
              const owned = pd?.hangar?.shipsUnlocked?.includes(ship.id);
              const equipped = pd?.hangar?.currentShip === ship.id;
              const highestCleared = (pd?.highestUnlocked ?? 1) - 1;
              const levelOk = highestCleared >= ship.levelRequired;
              const fpOk = fp >= ship.fpCost;

              return (
                <div key={ship.id} data-testid={`pd-ship-${ship.id}`}
                     className={`rounded-xl p-4 border transition-all ${
                       equipped ? 'border-cyan-300/60' : 'border-white/10 hover:border-white/25'
                     }`}
                     style={{
                       background: `linear-gradient(135deg, ${ship.body}18 0%, ${ship.glow}0d 100%)`,
                     }}>
                  <div className="flex items-start gap-3">
                    <ShipMiniIcon ship={ship}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="font-heading text-white text-lg leading-tight truncate">{ship.name}</p>
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full"
                              style={{ background: `${ship.body}22`, color: ship.glow }}>
                          {ship.trait}
                        </span>
                      </div>
                      <p className="text-white/55 text-xs mt-1 leading-relaxed">{ship.tagline}</p>

                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div className="text-[10px] uppercase tracking-widest text-white/45">
                          {ship.levelRequired > 0 && <>Lv {ship.levelRequired} · </>}
                          {ship.fpCost > 0 ? <>{ship.fpCost} FP</> : <>Free</>}
                        </div>
                        {owned ? (
                          equipped ? (
                            <span className="inline-flex items-center gap-1 text-xs text-cyan-300 font-semibold">
                              <Check size={12}/> Equipped
                            </span>
                          ) : (
                            <button type="button" onClick={() => onEquipShip(ship.id)}
                                    data-testid={`pd-ship-${ship.id}-equip`}
                                    className="px-3 py-1 rounded-full text-xs font-semibold bg-white/90 text-navy hover:brightness-95">
                              Equip
                            </button>
                          )
                        ) : (
                          <button type="button"
                                  disabled={!levelOk || !fpOk}
                                  onClick={() => onUnlockShip(ship)}
                                  data-testid={`pd-ship-${ship.id}-unlock`}
                                  className={`px-3 py-1 rounded-full text-xs font-semibold transition-smooth ${
                                    (!levelOk || !fpOk)
                                      ? 'bg-white/10 text-white/35 cursor-not-allowed'
                                      : 'bg-cyan-300 text-navy hover:brightness-95'
                                  }`}>
                            {!levelOk ? `Clear Lv ${ship.levelRequired}` : !fpOk ? 'Need FP' : 'Unlock'}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/** Small neon triangle preview for the hangar cards. */
function ShipMiniIcon({ ship }) {
  return (
    <div className="w-14 h-14 rounded-xl flex items-center justify-center shrink-0"
         style={{
           background: `radial-gradient(circle at 50% 50%, ${ship.glow}30, transparent 70%)`,
           boxShadow: `inset 0 0 12px ${ship.body}33`,
         }}>
      <svg viewBox="0 0 40 40" width="40" height="40">
        <defs>
          <filter id={`glow-${ship.id}`} x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1.4" result="b"/>
            <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
          </filter>
        </defs>
        <polygon points="20,4 34,32 20,26 6,32"
                 fill={ship.body}
                 stroke={ship.glow}
                 strokeWidth="1.5"
                 strokeLinejoin="round"
                 filter={`url(#glow-${ship.id})`} />
        <circle cx="20" cy="32" r="2.5" fill={ship.engine} opacity="0.9"/>
      </svg>
    </div>
  );
}
